import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Save, Upload, Trash2, Plus, ArrowUpRight, ExternalLink, Mail, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

function ImageField({ value, onChange, label, testId }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(res.data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {label && <label className="block text-xs uppercase tracking-widest text-neutral-500 mb-2 font-medium">{label}</label>}
      <div className="flex gap-3 items-start">
        <div className="w-24 h-24 border border-neutral-300 bg-neutral-100 overflow-hidden shrink-0">
          {value ? (
            <img src={value} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-xs text-neutral-400">No image</div>
          )}
        </div>
        <div className="flex-1 space-y-2">
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Image URL or data:image/…"
            className="w-full text-xs border border-neutral-300 outline-none focus:border-orange-600 px-3 py-2 font-mono"
            data-testid={testId ? `${testId}-url` : undefined}
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-2 text-xs bg-neutral-900 text-white px-3 py-2 hover:bg-orange-600 transition-colors"
              data-testid={testId ? `${testId}-upload` : undefined}
            >
              <Upload className="w-3 h-3" />
              {uploading ? "Uploading…" : "Upload"}
            </button>
            {value && (
              <button
                type="button"
                onClick={() => onChange("")}
                className="inline-flex items-center gap-1 text-xs border border-neutral-300 text-neutral-600 px-3 py-2 hover:bg-neutral-100"
              >
                <Trash2 className="w-3 h-3" />Clear
              </button>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
          />
        </div>
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, textarea, testId, ...rest }) {
  const Cmp = textarea ? "textarea" : "input";
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-neutral-500 mb-2 font-medium">{label}</label>
      <Cmp
        {...rest}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={textarea ? 4 : undefined}
        className="w-full border border-neutral-300 outline-none focus:border-orange-600 px-4 py-3 font-body"
        data-testid={testId}
      />
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { content, save, refresh } = useContent();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [messages, setMessages] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    if (content) setForm(JSON.parse(JSON.stringify(content)));
  }, [content]);

  const loadMessages = async () => {
    try {
      const res = await api.get("/contact");
      setMessages(res.data);
    } catch (err) {
      toast.error("Failed to load messages");
    }
  };

  useEffect(() => {
    loadMessages();
  }, []);

  if (!form) return null;

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const saveAll = async () => {
    setSaving(true);
    try {
      await save(form);
      toast.success("Changes saved to live site");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    nav("/admin/login");
  };

  // Services helpers
  const updateService = (idx, patch) => {
    const services = [...form.services];
    services[idx] = { ...services[idx], ...patch };
    set({ services });
  };
  const removeService = (idx) => {
    const services = form.services.filter((_, i) => i !== idx);
    set({ services });
  };
  const addService = () => {
    set({
      services: [
        ...form.services,
        { id: crypto.randomUUID(), title: "New Service", description: "Description…", image: "", icon: "hammer" },
      ],
    });
  };

  // Gallery helpers
  const updateGallery = (idx, patch) => {
    const gallery = [...form.gallery];
    gallery[idx] = { ...gallery[idx], ...patch };
    set({ gallery });
  };
  const removeGallery = (idx) => set({ gallery: form.gallery.filter((_, i) => i !== idx) });
  const addGallery = () => set({ gallery: [...form.gallery, { id: crypto.randomUUID(), image: "", caption: "" }] });

  // Testimonials helpers
  const updateT = (idx, patch) => {
    const testimonials = [...form.testimonials];
    testimonials[idx] = { ...testimonials[idx], ...patch };
    set({ testimonials });
  };
  const removeT = (idx) => set({ testimonials: form.testimonials.filter((_, i) => i !== idx) });
  const addT = () => set({ testimonials: [...form.testimonials, { id: crypto.randomUUID(), name: "", role: "", quote: "", rating: 5 }] });

  const markRead = async (id) => {
    await api.patch(`/contact/${id}/read`);
    loadMessages();
  };
  const deleteMsg = async (id) => {
    if (!confirm("Delete this message?")) return;
    await api.delete(`/contact/${id}`);
    loadMessages();
    toast.success("Message deleted");
  };

  const unreadCount = messages.filter((m) => !m.read).length;

  return (
    <div className="min-h-screen bg-neutral-100" data-testid="admin-dashboard-page">
      {/* Header */}
      <header className="bg-neutral-950 text-white border-b border-neutral-800 sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {form.logo_url && <img src={form.logo_url} alt="" className="h-10 w-auto" />}
            <div>
              <div className="text-xs uppercase tracking-widest text-orange-500">MER Home Improvement</div>
              <div className="font-display uppercase font-bold text-lg leading-none">Admin Dashboard</div>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <a href="/" target="_blank" rel="noreferrer" className="hidden md:inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-white px-4 py-2 border border-neutral-800" data-testid="view-site-link">
              <ExternalLink className="w-4 h-4" />View site
            </a>
            <button
              onClick={saveAll}
              disabled={saving}
              className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-display font-bold uppercase text-sm tracking-wider px-4 py-2.5 disabled:opacity-60"
              data-testid="admin-save-btn"
            >
              <Save className="w-4 h-4" />
              {saving ? "Saving…" : "Save Changes"}
            </button>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-white px-3 py-2.5 border border-neutral-800"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
        <div className="mb-6 text-sm text-neutral-500">
          Signed in as <strong className="text-neutral-900">{user?.email}</strong>
        </div>

        <Tabs defaultValue="hero" className="w-full">
          <TabsList className="mb-6 flex flex-wrap h-auto bg-white border border-neutral-200 p-1" data-testid="admin-tabs">
            <TabsTrigger value="hero" data-testid="tab-hero">Hero & Logo</TabsTrigger>
            <TabsTrigger value="about" data-testid="tab-about">About</TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-services">Services</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery">Gallery</TabsTrigger>
            <TabsTrigger value="testimonials" data-testid="tab-testimonials">Testimonials</TabsTrigger>
            <TabsTrigger value="contact-info" data-testid="tab-contact-info">Contact Info</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages">
              Messages {unreadCount > 0 && <span className="ml-2 bg-orange-600 text-white text-xs px-1.5 py-0.5">{unreadCount}</span>}
            </TabsTrigger>
          </TabsList>

          {/* HERO */}
          <TabsContent value="hero" className="bg-white border border-neutral-200 p-8 space-y-6">
            <h2 className="font-display uppercase text-2xl font-bold">Hero Section & Branding</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <TextField label="Business Name" value={form.business_name} onChange={(v) => set({ business_name: v })} testId="input-business-name" />
              <TextField label="Tagline" value={form.tagline} onChange={(v) => set({ tagline: v })} testId="input-tagline" />
            </div>
            <TextField label="Hero Headline" value={form.hero_headline} onChange={(v) => set({ hero_headline: v })} testId="input-hero-headline" />
            <TextField label="Hero Subheadline" value={form.hero_subheadline} onChange={(v) => set({ hero_subheadline: v })} textarea testId="input-hero-subheadline" />
            <div className="grid md:grid-cols-2 gap-6">
              <ImageField label="Logo (used in hero & navbar)" value={form.logo_url} onChange={(v) => set({ logo_url: v })} testId="logo" />
              <ImageField label="Hero Background Image" value={form.hero_background} onChange={(v) => set({ hero_background: v })} testId="hero-bg" />
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <TextField label="Years Experience" type="number" value={form.years_experience} onChange={(v) => set({ years_experience: Number(v) })} />
              <TextField label="Projects Completed" type="number" value={form.projects_completed} onChange={(v) => set({ projects_completed: Number(v) })} />
              <TextField label="Happy Clients" type="number" value={form.happy_clients} onChange={(v) => set({ happy_clients: Number(v) })} />
            </div>
          </TabsContent>

          {/* ABOUT */}
          <TabsContent value="about" className="bg-white border border-neutral-200 p-8 space-y-6">
            <h2 className="font-display uppercase text-2xl font-bold">About Section</h2>
            <TextField label="About Title" value={form.about_title} onChange={(v) => set({ about_title: v })} testId="input-about-title" />
            <TextField label="About Text" value={form.about_text} onChange={(v) => set({ about_text: v })} textarea testId="input-about-text" />
          </TabsContent>

          {/* SERVICES */}
          <TabsContent value="services" className="bg-white border border-neutral-200 p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display uppercase text-2xl font-bold">Services</h2>
              <button onClick={addService} className="inline-flex items-center gap-2 bg-neutral-900 text-white text-sm px-4 py-2 hover:bg-orange-600 transition-colors" data-testid="add-service-btn">
                <Plus className="w-4 h-4" />Add Service
              </button>
            </div>
            <div className="space-y-8">
              {form.services.map((s, i) => (
                <div key={s.id || i} className="border border-neutral-200 p-6 grid md:grid-cols-12 gap-4" data-testid={`service-editor-${i}`}>
                  <div className="md:col-span-4">
                    <ImageField value={s.image} onChange={(v) => updateService(i, { image: v })} testId={`service-${i}-image`} />
                  </div>
                  <div className="md:col-span-7 space-y-3">
                    <TextField label="Title" value={s.title} onChange={(v) => updateService(i, { title: v })} testId={`service-${i}-title`} />
                    <TextField label="Description" value={s.description} onChange={(v) => updateService(i, { description: v })} textarea testId={`service-${i}-desc`} />
                  </div>
                  <div className="md:col-span-1 flex md:justify-end">
                    <button onClick={() => removeService(i)} className="text-red-600 hover:bg-red-50 border border-red-200 p-2" data-testid={`remove-service-${i}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* GALLERY */}
          <TabsContent value="gallery" className="bg-white border border-neutral-200 p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display uppercase text-2xl font-bold">Gallery</h2>
              <button onClick={addGallery} className="inline-flex items-center gap-2 bg-neutral-900 text-white text-sm px-4 py-2 hover:bg-orange-600 transition-colors" data-testid="add-gallery-btn">
                <Plus className="w-4 h-4" />Add Image
              </button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {form.gallery.map((g, i) => (
                <div key={g.id || i} className="border border-neutral-200 p-4 space-y-3" data-testid={`gallery-editor-${i}`}>
                  <ImageField value={g.image} onChange={(v) => updateGallery(i, { image: v })} testId={`gallery-${i}-image`} />
                  <TextField label="Caption" value={g.caption} onChange={(v) => updateGallery(i, { caption: v })} />
                  <button onClick={() => removeGallery(i)} className="text-sm text-red-600 hover:underline flex items-center gap-1" data-testid={`remove-gallery-${i}`}>
                    <Trash2 className="w-3 h-3" />Remove
                  </button>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* TESTIMONIALS */}
          <TabsContent value="testimonials" className="bg-white border border-neutral-200 p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display uppercase text-2xl font-bold">Testimonials</h2>
              <button onClick={addT} className="inline-flex items-center gap-2 bg-neutral-900 text-white text-sm px-4 py-2 hover:bg-orange-600 transition-colors" data-testid="add-testimonial-btn">
                <Plus className="w-4 h-4" />Add Testimonial
              </button>
            </div>
            <div className="space-y-4">
              {form.testimonials.map((t, i) => (
                <div key={t.id || i} className="border border-neutral-200 p-6 grid md:grid-cols-12 gap-4" data-testid={`testimonial-editor-${i}`}>
                  <div className="md:col-span-4 space-y-3">
                    <TextField label="Name" value={t.name} onChange={(v) => updateT(i, { name: v })} />
                    <TextField label="Role / Location" value={t.role} onChange={(v) => updateT(i, { role: v })} />
                    <TextField label="Rating (1-5)" type="number" value={t.rating} onChange={(v) => updateT(i, { rating: Math.max(1, Math.min(5, Number(v))) })} />
                  </div>
                  <div className="md:col-span-7">
                    <TextField label="Quote" value={t.quote} onChange={(v) => updateT(i, { quote: v })} textarea />
                  </div>
                  <div className="md:col-span-1 flex md:justify-end">
                    <button onClick={() => removeT(i)} className="text-red-600 hover:bg-red-50 border border-red-200 p-2" data-testid={`remove-testimonial-${i}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* CONTACT INFO */}
          <TabsContent value="contact-info" className="bg-white border border-neutral-200 p-8 space-y-6">
            <h2 className="font-display uppercase text-2xl font-bold">Contact Information</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <TextField label="Phone" value={form.phone} onChange={(v) => set({ phone: v })} testId="input-phone" />
              <TextField label="Email" value={form.email} onChange={(v) => set({ email: v })} testId="input-email" />
            </div>
            <TextField label="Service Area / Location" value={form.location} onChange={(v) => set({ location: v })} testId="input-location" />
          </TabsContent>

          {/* MESSAGES */}
          <TabsContent value="messages" className="bg-white border border-neutral-200 p-8">
            <h2 className="font-display uppercase text-2xl font-bold mb-6">Contact Messages</h2>
            {messages.length === 0 ? (
              <div className="text-neutral-500 py-16 text-center border border-dashed border-neutral-300">
                No messages yet.
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map((m) => (
                  <div key={m.id} className={`border p-5 ${m.read ? "border-neutral-200 bg-neutral-50" : "border-orange-500 bg-orange-50"}`} data-testid={`message-${m.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-orange-600" />
                          <span className="font-display font-bold uppercase">{m.name}</span>
                          {!m.read && <span className="text-[10px] bg-orange-600 text-white uppercase tracking-widest px-1.5 py-0.5">New</span>}
                        </div>
                        <div className="text-sm text-neutral-500 mt-1">
                          {m.email} {m.phone && ` · ${m.phone}`} {m.service && ` · ${m.service}`}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {!m.read && (
                          <button onClick={() => markRead(m.id)} className="text-xs text-neutral-600 border border-neutral-300 px-3 py-1.5 hover:bg-white" data-testid={`mark-read-${m.id}`}>
                            <CheckCircle2 className="w-3 h-3 inline mr-1" />Mark read
                          </button>
                        )}
                        <button onClick={() => deleteMsg(m.id)} className="text-xs text-red-600 border border-red-200 px-3 py-1.5 hover:bg-red-50" data-testid={`delete-msg-${m.id}`}>
                          <Trash2 className="w-3 h-3 inline mr-1" />Delete
                        </button>
                      </div>
                    </div>
                    <p className="mt-3 text-neutral-700 leading-relaxed">{m.message}</p>
                    <div className="mt-2 text-xs text-neutral-400">{new Date(m.created_at).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
